/**
 * dscTabFilter.js
 *
 * Per-tab product filter for the DSC results section. Translates the
 * declarative `DSC_TAB_PRODUCT_CONFIG` (in `data/dscSlotConfig.js`) plus the
 * live cascade selections into the product subset that belongs in each
 * results tab.
 *
 * Two exports:
 *   - filterProductsForTab(allProducts, tabSlug, cascadeSelections, filteringData)
 *       → returns the filtered product list for ONE tab.
 *   - computeTabCounts(allProducts, tabOrder, cascadeSelections, filteringData)
 *       → returns `{ [tabSlug]: count }` for every tab in `tabOrder`.
 *
 * `filteringData` is optional; when supplied it enables reverse-derivation
 * for the DTH bit-first entry (task #22) — see dth-hammers branch below.
 *
 * Two-phase filter:
 *   Phase 1 — PGMJ + PGMN tuple — narrows to the product family for the tab
 *             (e.g. shank-adapters tab = pgmj=184 + pgmn=T20, NOT the whole
 *             pgmj=184 set which also contains barrel/coupling sleeves).
 *   Phase 2 — cascade-selection narrowing — applies tab-specific rules
 *             derived from the legacy cascade in `searchResults.js:1206-1729`
 *             and the matching service in `dscMatchingService.js`. Only
 *             narrows when the relevant selection is non-null.
 *
 * Sources of truth:
 *  - PGMJ/PGMN tuples per tab — Apex queries in `DSCController.cls`:
 *      shank-adapters    getThreadInformation(... '184', 'T20')          line 187
 *      coupling-sleeve   getCouplingThreadforRods(... '184', 'T24')      line 339
 *      rod-extensions    rod cascade (slot config pgmj=184, pgmn=T28)
 *      th-bits           getCouplingThreadforBits(... '182')             line 219
 *      dth-bits          getFilteringData (pgmj=832, pgmn in D04/D14)    line 49
 *      dth-hammers       getDTHHammerData (pgmj=830, pgmn in D45/D55)    line 92
 *      dth-drill-pipes   getDTHDrillData (pgmj=849)                       line 126
 *  - Cascade narrowing rules — legacy `searchResults.js:1240-1729`
 *    reducer branches, condensed per tab.
 *  - bit-adapters tab pgmj/pgmn TBD per `dscSlotConfig.js:103-105` — the
 *    config returns an empty list until master-data confirms the codes,
 *    matching the explicit "(TBD)" note already in the slot config.
 *
 * @module services/dscTabFilter
 * @since 2026-05-20 (Group E SP6 — post-walkthrough cascade-results wiring)
 */

import { DSC_TAB_PRODUCT_CONFIG } from '@data/dscSlotConfig';

// ============================================================================
// Helpers
// ============================================================================

function splitSemicolon(val) {
  if (val == null) return [];
  return String(val)
    .split(';')
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

/**
 * In-scope DTH hammer families for the MRT DSC (business rule 20 May 2026).
 * Mirrors `DTH_DSC_IN_SCOPE_FAMILIES` in dscMatchingService.js — duplicated
 * here so the tab filter can enforce the rule on top of the subcategory base
 * without an import cycle.
 */
const DTH_DSC_IN_SCOPE_FAMILIES = ['RH560', 'RH570'];

/**
 * Parse the two thread ends out of a bit-adapter description such as
 * "BIT ADAPTER T38F/T45M" or "BIT ADAPTER T51F/GT60M". Strips the F/M
 * suffix and returns the two thread tokens as strings. Returns [] when
 * the description doesn't match the BIT ADAPTER pattern.
 */
export function parseBitAdapterThreads(description) {
  if (!description) return [];
  const m = String(description).match(/BIT ADAPTER\s+(.+)$/i);
  if (!m) return [];
  const tail = m[1].trim();
  // First token contains the two-thread spec, e.g. "T38F/T45M"
  const spec = tail.split(/\s+/)[0];
  if (!spec.includes('/')) return [];
  return spec
    .split('/')
    .map((part) => part.replace(/[FM]$/i, '').trim())
    .filter(Boolean);
}

/**
 * True when the bit-adapter is compatible with the picked rod thread —
 * description-substring OR thread-field match. Description-substring is the
 * primary signal because PWA `thread` field on bit adapters is sparsely
 * populated (9 of 17 actual bit adapters carry it). Vandana 20 May:
 * "should be both thread and description ... match with the rods selected".
 */
function bitAdapterMatchesRod(product, rodThread) {
  if (!product || !rodThread) return false;
  // Exclude non-bit-adapters (guide adapters, barrel sleeves) by description.
  const desc = String(product.description || '').toUpperCase();
  if (!desc.includes('BIT ADAPTER')) return false;
  const ends = parseBitAdapterThreads(product.description);
  if (ends.includes(rodThread)) return true;
  if (product.thread === rodThread) return true;
  if (product.thread1 === rodThread) return true;
  if (product.thread2 === rodThread) return true;
  return false;
}

/**
 * Return the bit-adapter's "other end" thread given the rod thread it was
 * matched on. Used to populate the bit-adapter:thread dropdown — the user
 * picks the thread for the bit side of the adapter.
 */
export function bitAdapterOtherEndThread(product, rodThread) {
  const ends = parseBitAdapterThreads(product?.description);
  if (ends.length === 2) {
    const other = ends.find((t) => t !== rodThread);
    if (other) return other;
  }
  // Fallback to any explicit thread field that isn't the rod thread.
  for (const f of ['thread', 'thread1', 'thread2']) {
    if (product?.[f] && product[f] !== rodThread) return product[f];
  }
  return null;
}

// ============================================================================
// Public API
// ============================================================================

/**
 * Filter the full product list down to those that belong in `tabSlug`,
 * further narrowed by any cascade selections that apply to that tab.
 *
 * Returns [] for unknown tabs, for tabs whose pgmj is TBD (bit-adapters), or
 * when `allProducts` is not an array.
 *
 * @param {Object[]} allProducts
 * @param {string} tabSlug                     e.g. 'shank-adapters', 'dth-bits'
 * @param {Record<string, any>} cascadeSelections   e.g. state.selections
 * @returns {Object[]}
 */
export function filterProductsForTab(allProducts, tabSlug, cascadeSelections = {}, filteringData = []) {
  const config = DSC_TAB_PRODUCT_CONFIG[tabSlug];
  if (!config) return [];
  if (!Array.isArray(allProducts)) return [];

  // -------------------------------------------------------------------------
  // Phase 1 — subcategory (TH) / category (DTH) base filter
  // -------------------------------------------------------------------------
  // The PWA enriched JSON groups products via `subcategory` (TH) and
  // `category` (DTH). dataService.loadAllData already keys on these, so the
  // tab filter mirrors them. Rewritten 20 May 2026 after data audit showed
  // the legacy (pgmj, pgmn) tuple maps unreliably in PWA data — see
  // DSC_TAB_PRODUCT_CONFIG header comment in data/dscSlotConfig.js.
  let products = allProducts.filter((p) => {
    if (Array.isArray(config.subcategories) && config.subcategories.length > 0) {
      return config.subcategories.includes(p?.subcategory);
    }
    if (Array.isArray(config.categories) && config.categories.length > 0) {
      return config.categories.includes(p?.category);
    }
    return false;
  });

  // -------------------------------------------------------------------------
  // Phase 2 — cascade-selection narrowing (tab-specific)
  // -------------------------------------------------------------------------
  const sel = cascadeSelections || {};

  switch (tabSlug) {
    case 'shank-adapters': {
      const s = sel['shank-adapter'];
      if (s?.mfr) {
        products = products.filter((p) => p.rockDrillManufacturer === s.mfr);
      }
      if (s?.model) {
        products = products.filter((p) =>
          splitSemicolon(p.compatibleRockDrill).includes(s.model)
        );
      }
      break;
    }

    case 'coupling-sleeve': {
      const c = sel['couplings'];
      if (c?.type) {
        products = products.filter((p) => p.reductionCoupling === c.type);
      }
      if (c?.rodThread) {
        products = products.filter((p) => p.threadF2 === c.rodThread);
      }
      break;
    }

    case 'rod-extensions': {
      const r = sel['rod'];
      if (r?.thread) {
        // Rods carry thread1 + thread2 (both ends) — single-thread rods have
        // thread1 === thread2, multi-thread rods (reduction-style) carry
        // distinct ends. Either end matching the picked rod thread makes the
        // rod compatible (the user can orient the rod with the matching end
        // toward the bit). Data audit 20 May confirmed all pgmj=182
        // subcategories carry both fields.
        products = products.filter(
          (p) => p.thread1 === r.thread || p.thread2 === r.thread
        );
      }
      break;
    }

    case 'bit-adapters': {
      // Step 1: exclude non-bit-adapters (guide adapters, barrel sleeves) —
      // the pilot-bit-adapters subcategory mixes them all. Vandana 20 May:
      // "we want to exclude guide adapter, barrel sleeves etc".
      products = products.filter((p) =>
        String(p?.description || '').toUpperCase().includes('BIT ADAPTER')
      );
      // Step 2: narrow by the rod thread the user has picked (since bit
      // adapters connect rod → bit, the rod-side end must match).
      const rodSel = sel['rod'];
      if (rodSel?.thread) {
        products = products.filter((p) => bitAdapterMatchesRod(p, rodSel.thread));
      }
      // Step 3: when user has picked an adapter-thread (the bit-side end),
      // narrow further to bit adapters whose other end matches that pick.
      const a = sel['bit-adapter'];
      if (a?.thread) {
        products = products.filter((p) => {
          const otherEnd = bitAdapterOtherEndThread(p, rodSel?.thread);
          return otherEnd === a.thread || p.thread === a.thread;
        });
      }
      break;
    }

    case 'th-bits': {
      const r = sel['rod'];
      const b = sel['drill-bit'];
      if (r?.thread) {
        products = products.filter((p) => p.thread === r.thread);
      }
      if (b?.type) {
        products = products.filter((p) => p.buttonShape === b.type);
      }
      break;
    }

    case 'dth-hammers': {
      // Business rule (20 May 2026): MRT DSC supports RH560 + RH570 only —
      // RH510 and RH460 use different matching logic and are excluded.
      products = products.filter(
        (p) => p?.hammerFamily && DTH_DSC_IN_SCOPE_FAMILIES.includes(p.hammerFamily)
      );
      const h = sel['hammer-series'];
      // The Hammer Series dropdown stores its value as the full label
      // 'RH570-6.5"' (family + size concatenated, set by reduceDTHHammerSlot
      // line 506). The PRODUCT carries hammerFamily='RH570' separately from
      // hammerSize_in='6.5"' — so we must split the label before comparing.
      // The cascade reducer ALSO populates h.size as a separate field; use
      // it for the size narrowing.
      if (h?.family) {
        const [familyName] = String(h.family).split('-');
        if (familyName) {
          products = products.filter((p) => p.hammerFamily === familyName);
        }
      }
      if (h?.size) {
        products = products.filter(
          (p) => String(p.hammerSize_in) === String(h.size)
        );
      }
      if (h?.shank) {
        products = products.filter((p) =>
          splitSemicolon(p.compatibleShankType).includes(h.shank)
        );
      } else {
        // Task #22 — DTH bit-first reverse derivation.
        // When the user picks dth-bit.holeSize before hammer-series.shank
        // (bit-first entry per searchResults.js:1477-1496), narrow the
        // dth-hammers tab to hammers whose compatibleShankType intersects
        // the set of shank types filteringData considers valid for that
        // hole size. Without this, the tab defaults to the full
        // RH560+RH570 catalog even though only a subset accepts the
        // picked bit.
        const b = sel['dth-bit'];
        if (b?.holeSize && Array.isArray(filteringData) && filteringData.length > 0) {
          const compatibleShanks = new Set();
          for (const item of filteringData) {
            if (item?.shankType && String(item.bitDiameter) === String(b.holeSize)) {
              compatibleShanks.add(item.shankType);
            }
          }
          if (compatibleShanks.size > 0) {
            products = products.filter((p) =>
              splitSemicolon(p.compatibleShankType).some((s) => compatibleShanks.has(s))
            );
          }
        }
      }
      break;
    }

    case 'dth-drill-pipes': {
      const d = sel['drillpipe'];
      if (d?.pinOption) {
        products = products.filter((p) => p.topsubPinOption === d.pinOption);
      }
      break;
    }

    case 'dth-bits': {
      const b = sel['dth-bit'];
      const h = sel['hammer-series'];
      if (b?.holeSize) {
        // The hole-size value is stored as the canonical bit diameter from the
        // matching service (either _in or _mm depending on unit toggle). Match
        // against both columns defensively so the toggle flip doesn't break
        // narrowing.
        products = products.filter(
          (p) =>
            String(p.bitDiameter_in) === String(b.holeSize) ||
            String(p.bitDiameter_mm) === String(b.holeSize)
        );
      }
      if (h?.shank) {
        products = products.filter((p) =>
          splitSemicolon(p.compatibleShankType).includes(h.shank)
        );
      }
      break;
    }

    default:
      break;
  }

  return products;
}

/**
 * Compute counts for every tab in `tabOrder`, used by DSCResultsTabs to
 * render the `(N)` badge per tab. Cheap because filtering is in-memory and
 * `tabOrder` is at most 5-element (TH) / 3-element (DTH).
 *
 * @param {Object[]} allProducts
 * @param {string[]} tabOrder
 * @param {Record<string, any>} cascadeSelections
 * @returns {Record<string, number>}
 */
export function computeTabCounts(allProducts, tabOrder, cascadeSelections = {}, filteringData = []) {
  const counts = {};
  if (!Array.isArray(tabOrder)) return counts;
  for (const tabSlug of tabOrder) {
    counts[tabSlug] = filterProductsForTab(allProducts, tabSlug, cascadeSelections, filteringData).length;
  }
  return counts;
}
